import { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import type { OrderResult } from '../services/orderService';
import type { CartItem } from '../types';
import { gtm } from '../services/gtm';

/**
 * Order confirmation page displayed after successful order submission.
 * Shows the master order number and a thank you message.
 */
export function OrderConfirmation() {
  const { masterOrderNo } = useParams<{ masterOrderNo: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const orders: OrderResult[] = location.state?.orders ?? [];
  const cart: CartItem[] = location.state?.cart ?? [];

  useEffect(() => {
    if (masterOrderNo && orders.length > 0) {
      gtm.pushPurchase(masterOrderNo, orders, cart);
    }
  }, [masterOrderNo]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        {/* Success Icon */}
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Thank You for Your Order!
        </h1>

        <p className="text-lg text-gray-600 mb-6">
          Your order has been successfully placed.
        </p>

        {orders.length > 0 ? (
          <div className="bg-gray-50 rounded-lg p-4 mb-8 inline-block text-left">
            <p className="text-sm text-gray-500 mb-2">{orders.length === 1 ? 'Order Number' : 'Order Numbers'}</p>
            <div className="space-y-1">
              {orders.map((order) => (
                <a
                  key={order.orderNo}
                  href={order.receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xl font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {order.orderNo}
                </a>
              ))}
            </div>
          </div>
        ) : masterOrderNo ? (
          <div className="bg-gray-50 rounded-lg p-4 mb-8 inline-block">
            <p className="text-sm text-gray-500 mb-1">Order Number</p>
            <p className="text-xl font-semibold text-gray-900">{masterOrderNo}</p>
          </div>
        ) : null}

        <div className="space-y-4 text-gray-600 mb-8">
          <p>
            A confirmation email has been sent to your email address with your order details.
          </p>
          <p>
            You can track your order status using the order number above.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
}
